<template>
    <BaseCard
        as="div"
        theme="cream"
        padding="sm"
        radius="lgr"
        class="flex w-full min-w-0 max-w-[600px] items-center gap-3 text-left md:w-full"
        data-testid="document-creator-widget">
        <span class="icon" aria-hidden="true"><i class="bi bi-file-text-fill"></i></span>
        <div class="flex min-w-0 flex-1 flex-col">
            <span class="nadia-chat-bold block break-words text-ink">{{ action.action_params.document.title }}</span>
            <span class="scaffold-details-small text-ink-muted">{{ t("chat.documentCreator.kind") }}</span>
        </div>
        <BaseButton theme="neutral" size="sm" class="shrink-0" data-testid="document-creator-preview-button" @click="showPreview = true">
            {{ t("chat.documentCreator.open") }}
        </BaseButton>
    </BaseCard>
    <!-- Preview Modal -->
    <BaseDialog v-model:open="showPreview" :title="t('chat.documentCreator.previewTitle')" width="wide" @close="closePreview">
        <div data-testid="document-creator-preview-modal" class="bg-surface-raised border-2 border-border min-h-[60dvh] p-6 md:p-10">
            <div v-if="fullContentLoading" class="text-ink-muted" data-testid="document-creator-full-content-loading">{{ t("chat.documentCreator.loadingFull") }}</div>
            <MarkdownText v-else-if="isMarkdown" :text="previewContent" class="text-ink" data-testid="document-creator-full-content" />
            <div v-else class="text-ink whitespace-pre-line" data-testid="document-creator-full-content">{{ previewContent }}</div>
        </div>
        <template #footer>
            <BaseNotification
                v-if="showErrorNotification"
                class="w-full"
                :text="errorMessage"
                :theme="errorMessage.includes('successfully') ? 'success' : 'error'"
                @dismiss="showErrorNotification = !showErrorNotification" />
            <BaseDropdownMenu :sections="emailFormatSections" align="start" :side-offset="2" @select="handleEmailPreviewSelect">
                <template #trigger>
                    <BaseButton :aria-label="t('chat.documentCreator.emailAriaLabel')" theme="tertiary" icon-only :disabled="emailLoading">
                        <i class="bi bi-envelope text-[24px]" aria-hidden="true"></i>
                    </BaseButton>
                </template>
            </BaseDropdownMenu>
            <BaseDropdownMenu v-if="canDownload" :sections="downloadFormatSections" align="start" :side-offset="2" @select="handleDownloadPreviewSelect">
                <template #trigger>
                    <BaseButton :aria-label="t('chat.documentCreator.downloadAriaLabel')" theme="tertiary" icon-only data-testid="document-creator-download-button">
                        <i class="bi bi-download text-[24px]" aria-hidden="true"></i>
                    </BaseButton>
                </template>
            </BaseDropdownMenu>
        </template>
    </BaseDialog>
</template>

<script setup>
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { canShareRestrictedFiles, isMobileSharingRestricted, shareRestrictedFile } from "~vue/utils/nativeSharingPolicy";
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    action: {
        type: Object,
        required: true,
    },
    lineIdx: { type: Number, required: true },
    messageId: { type: Number, required: true },
});

const { t } = useI18n();
const { emitter } = inject("globalProperties");
const organizationDetails = inject("organization_details", null);

const isSharingRestricted = computed(() => isMobileSharingRestricted(organizationDetails?.value));
const canDownload = computed(() => !isSharingRestricted.value || canShareRestrictedFiles());

// Hard coding the supported formats for now
const downloadFormatOptions = ["PDF", "DOCX", "TXT", "XLSX"];

const formatSections = (icon) => [{ id: "formats", items: downloadFormatOptions.map((format) => ({ id: format.toLowerCase(), type: "button", label: format, icon })) }];
const emailFormatSections = formatSections("bi bi-envelope");
const downloadFormatSections = formatSections("bi bi-download");

// The API and analytics take the display casing ("PDF"), which is the item label.
const handleEmailPreviewSelect = (item) => emailAndClosePreview(item.label);
const handleDownloadPreviewSelect = (item) => downloadAndClosePreview(item.label);

const showPreview = ref(false);
const coachingSessionUuid = inject("coachingSessionUuid");
const showErrorNotification = ref(false);
const errorMessage = ref("");
const emailLoading = ref(false);

// For large documents the backend ships only a bounded preview inline (with content_truncated set)
// to keep the realtime payload small; the full content is fetched on demand for the preview modal.
const fullContent = ref(null);
const fullContentLoading = ref(false);

const isContentTruncated = computed(() => props.action.action_params.document.content_truncated === true);

// Only PDF content is authored as Markdown; DOCX/TXT are plain text and XLSX is TSV, so their line layout is kept as-is.
const isMarkdown = computed(() => props.action.action_params.document.format === "pdf");

const previewContent = computed(() => {
    if (isContentTruncated.value && fullContent.value !== null) {
        return fullContent.value;
    }
    return props.action.action_params.document.content;
});

async function fetchFullContent() {
    if (!isContentTruncated.value || fullContent.value !== null || fullContentLoading.value) {
        return;
    }
    fullContentLoading.value = true;
    const { data, execute, onFetchError, onFetchResponse } = useFetch(`/api/chat/document/content?chat_message=${props.messageId}`, { immediate: false }).get().json();

    onFetchResponse(() => {
        if (data.value?.content != null) {
            fullContent.value = data.value.content;
        }
    });
    onFetchError(() => {
        logError(new Error("Failed to fetch full document content for preview"));
    });

    await execute();
    fullContentLoading.value = false;
}

// Lazy-load the full document the first time the preview opens for a truncated document.
watch(showPreview, (open) => {
    if (open) {
        fetchFullContent();
    }
});

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            resolve(result.split(",")[1] ?? "");
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

function showError(message) {
    if (!showPreview.value) {
        emitter.emit("error_notification", message);
    } else {
        errorMessage.value = message;
        showErrorNotification.value = true;
    }
}

function downloadAndClosePreview(option) {
    downloadDocument(option, "preview");
}

function emailAndClosePreview(option) {
    emailDocument(option, "preview");
}

function closePreview() {
    showPreview.value = false;
}

async function downloadDocument(format, source = "tool_call") {
    const baseErrorMessage = "Failed to download document";

    const requestData = ref({
        requested_format: format.toLowerCase(),
        chat_message: props.messageId,
    });

    const { data, execute, onFetchError, onFetchResponse } = useFetch("/api/chat/document/create", { immediate: false }).post(requestData).blob();

    onFetchError(() => {
        showError(baseErrorMessage);
        logUserInteraction("document_download_errored", { format, source }, coachingSessionUuid);
    });

    onFetchResponse(async (response) => {
        try {
            const contentDisposition = response.headers.get("Content-Disposition");

            if (!contentDisposition) {
                logError(new Error("Content-Disposition header not found for document creation response"));
                throw new Error(baseErrorMessage);
            }

            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (!filenameMatch || !filenameMatch[1]) {
                logError(new Error("Filename not found in Content-Disposition header"));
                throw new Error(baseErrorMessage);
            }

            let filename = filenameMatch[1].replace(/['"]/g, "");

            if (Capacitor.isNativePlatform()) {
                // WKWebView (iOS) and Android WebView ignore <a download>. Write the
                // blob to the device filesystem and hand it to the native share sheet
                // so the user can save it to Files / Downloads / etc.
                const base64 = await blobToBase64(data.value);
                await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
                const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
                if (isSharingRestricted.value) {
                    await shareRestrictedFile(uri);
                } else {
                    try {
                        await Share.share({ title: props.action.action_params.document.title, url: uri });
                    } catch {
                        // User dismissed the share sheet — not a real failure.
                    }
                }
            } else {
                const downloadUrl = globalThis.URL.createObjectURL(data.value);
                const downloadLink = document.createElement("a");
                downloadLink.href = downloadUrl;
                downloadLink.download = filename;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                globalThis.URL.revokeObjectURL(downloadUrl);
            }

            logUserInteraction("document_downloaded", { format, source }, coachingSessionUuid);
        } catch {
            showError(baseErrorMessage);
            logUserInteraction("document_download_errored", { format, source }, coachingSessionUuid);
        }
    });

    await execute();
}

async function emailDocument(format, source = "tool_call") {
    const baseErrorMessage = "Failed to email document";
    const successMessage = "Document emailed successfully! Check your inbox.";

    emailLoading.value = true;

    const requestData = ref({
        requested_format: format.toLowerCase(),
        chat_message: props.messageId,
    });

    const { data, execute, onFetchError, onFetchResponse, onFetchFinally } = useFetch("/api/chat/document/email", { immediate: false }).post(requestData).json();

    onFetchError(() => {
        showError(baseErrorMessage);
        logUserInteraction("document_email_errored", { format, source }, coachingSessionUuid);
    });

    onFetchResponse(() => {
        if (data.value?.success) {
            if (!showPreview.value) {
                emitter.emit("success_notification", successMessage);
            } else {
                errorMessage.value = successMessage;
                showErrorNotification.value = true;
            }

            logUserInteraction("document_emailed", { format, source }, coachingSessionUuid);
        } else {
            showError(data.value?.message || baseErrorMessage);
            logUserInteraction("document_email_errored", { format, source }, coachingSessionUuid);
        }
    });

    onFetchFinally(() => {
        emailLoading.value = false;
    });

    await execute();
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Neutral file tile, same as the artifact / Reflections cards. */
    .icon {
        @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-ink-muted;
    }
}
</style>
